import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AppShell, TopHeader } from "../components/AppShell";
import { Button, Card, Pill } from "../components/ui";
import { Ic } from "../components/icons";
import { LocationViews } from "../components/LocationViews";
import {
  FicheTabs,
  parseFicheView,
  viewToQueryString,
  type FicheView,
} from "../components/FicheTabs";
import { EnvironnementTab } from "../components/EnvironnementTab";
import { AnalyseIATab } from "../components/AnalyseIATab";
import { useNav } from "../context/NavContext";
import { useAuth } from "../context/AuthContext";
import {
  setFicheStarred,
  subscribeFiche,
  type FicheDoc,
} from "../lib/fiches";
import { recordRecentFiche } from "../lib/recentFiches";
import { parcelLabel } from "../lib/parcelMerge";
import { useRisques } from "../lib/risques";
import { useDvfComparables } from "../lib/dvf";
import { DvfMap } from "../components/DvfMap";
import { PdfExportModal } from "../pdf/PdfExportModal";
import { PluQuestionPanel } from "../components/PluQuestionPanel";
import type {
  CadastreParcel,
  DvfTransaction,
  ReglementSource,
  RiskEntry,
  RiskLevel,
} from "../lib/types";

export default function Fiche() {
  const { go, ficheId } = useNav();
  const { user, loading: authLoading } = useAuth();
  const [fiche, setFiche] = useState<FicheDoc | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "loaded" | "missing">(
    ficheId ? "loading" : "idle",
  );
  // 2 états distincts : modale ouverte (choix des sections) vs. génération
  // effective du PDF (après validation de la modale). Permet de laisser
  // la modale visible pendant le chargement de l'état pdfInclude et de
  // switcher sur l'overlay "Génération en cours" une fois que l'utilisateur
  // a validé.
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const handleOpenPdfModal = () => {
    if (!fiche || pdfBusy) return;
    setPdfModalOpen(true);
  };

  /**
   * Confirmation depuis la modale : l'agent a coché ses sections +
   * cliqué "Télécharger". On ferme la modale côté state (la modale elle-même
   * le fait via onClose après notre onConfirm), on active l'overlay
   * "Génération en cours" et on lance le pipeline.
   *
   * Dynamic import : `@react-pdf/renderer` pèse ~400 KB gzipped, dont
   * 99% des sessions n'ont pas besoin. On paie ce coût uniquement
   * quand l'agent clique "Télécharger" dans la modale.
   */
  const handleConfirmPdf = async (
    include: import("../pdf/pdfIncludeState").PdfIncludeState,
  ) => {
    if (!fiche) return;
    setPdfBusy(true);
    try {
      const { downloadFichePdf } = await import("../pdf/downloadFichePdf");
      await downloadFichePdf(fiche, { include });
    } catch (err) {
      console.error("[pdf] download failed", err);
      alert(
        `La génération du PDF a échoué : ${err instanceof Error ? err.message : String(err)}.\n\nOuvre la console DevTools pour le détail complet.`,
      );
      throw err; // remonte pour que la modale ne se ferme pas sur erreur
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    if (!ficheId) {
      setState("idle");
      setFiche(null);
      return;
    }
    if (authLoading) return;
    if (!user) {
      setState("missing");
      return;
    }
    setState("loading");
    const unsub = subscribeFiche(ficheId, (f) => {
      setFiche(f);
      setState(f ? "loaded" : "missing");
    });
    return () => unsub();
  }, [ficheId, user, authLoading]);

  // Track the recently-viewed LRU — records at most once per ficheId per
  // visit, regardless of how many Firestore snapshots fire after.
  useEffect(() => {
    if (ficheId && state === "loaded") {
      recordRecentFiche(ficheId);
    }
  }, [ficheId, state]);

  return (
    <AppShell active="fiches">
      <TopHeader>
        {fiche && (
          <button
            onClick={() => {
              void setFicheStarred(fiche.id, !fiche.starred);
            }}
            title={fiche.starred ? "Retirer des épinglées" : "Épingler"}
            className={`w-9 h-9 rounded-full grid place-items-center transition ${
              fiche.starred
                ? "bg-lime-200 text-ink-900"
                : "bg-paper hairline text-ink-600 hover:bg-ink-50"
            }`}
          >
            <Ic.Star size={14} />
          </button>
        )}
        <Button variant="soft" size="sm" icon={<Ic.Share size={13} />}>
          Partager
        </Button>
        <Button variant="soft" size="sm" icon={<Ic.Mail size={13} />}>
          Envoyer
        </Button>
        <Button
          variant="dark"
          size="sm"
          icon={<Ic.Download size={13} />}
          onClick={handleOpenPdfModal}
          disabled={!fiche || pdfBusy}
        >
          {pdfBusy ? "Génération…" : "PDF"}
        </Button>
      </TopHeader>

      <div className="px-4 sm:px-8 pt-4 sm:pt-5 flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={() => go("dashboard")}
          className="text-[12.5px] text-ink-600 hover:text-ink-900 flex items-center gap-1.5"
        >
          <Ic.Arrow size={12} className="rotate-180" /> Retour au dashboard
        </button>
        <div className="flex items-center gap-2">
          {fiche ? (
            <>
              <Pill
                tone="soft"
                icon={<Ic.Dot size={8} className="text-forest" />}
              >
                Fiche {fiche.id} ·{" "}
                {formatWhen(fiche.createdAt?.toDate() ?? null)}
              </Pill>
              <Pill tone="soft" icon={<Ic.Clock size={11} />}>
                PLU extrait le{" "}
                {formatDateShort(
                  fiche.reglement?.source?.extractedAt
                    ? new Date(fiche.reglement.source.extractedAt)
                    : null,
                )}
              </Pill>
            </>
          ) : (
            !ficheId && (
              <Pill tone="amber" icon={<Ic.Bolt size={11} />}>
                Aperçu — démarrez une nouvelle analyse pour générer la vôtre
              </Pill>
            )
          )}
        </div>
      </div>

      <div className="px-4 sm:px-8 py-4 sm:py-6 max-w-[1200px] mx-auto page-enter">
        {state === "loading" && <LoadingState />}
        {state === "missing" && (
          <MissingState
            onNew={() => go("nouvelle")}
            onHome={() => go("dashboard")}
            authMissing={!user}
          />
        )}
        {state === "loaded" && fiche && (
          <FicheSheet
            fiche={fiche}
            onDownloadPdf={handleOpenPdfModal}
            pdfBusy={pdfBusy}
          />
        )}
        {state === "idle" && (
          <DemoSheet onNew={() => go("nouvelle")} />
        )}
      </div>

      {/* Overlay pendant la génération du PDF. On fetch 10+ data sources
          en parallèle (branding, risques, DVF, 10 env blocks), puis
          React-PDF rend le Document — ça prend typiquement 5-15 s, plus
          si un upstream est lent. Sans feedback visible l'agent pense
          que l'app a freeze et reclique. Backdrop blur + spinner + texte
          centré, non-fermable (le finally du handler remet pdfBusy à
          false en cas de succès comme d'erreur). */}
      {pdfBusy ? <PdfGeneratingOverlay /> : null}

      {/* Modale d'export PDF — sélection des sections à inclure.
          S'ouvre en cliquant sur le bouton "PDF" du header ou
          "Télécharger le PDF" du footer sticky. Lit l'état pdfInclude
          actuel des blocks depuis Firestore à l'ouverture, persiste
          au click "Télécharger" avant de générer. Lazy-loaded ? Non —
          c'est un simple composant React ~8 KB gzipped, pas la peine
          de l'isoler. Ce qui pèse (React-PDF renderer) reste dans le
          chunk dynamic de downloadFichePdf. */}
      {fiche ? (
        <PdfExportModal
          fiche={fiche}
          open={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
          onConfirm={handleConfirmPdf}
        />
      ) : null}
    </AppShell>
  );
}

/* ---------- Loading / Missing ---------- */

const LoadingState = () => (
  <div className="bg-paper rounded-[20px] shadow-pop p-14 grid place-items-center">
    <div className="text-center">
      <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto animate-pulse">
        <Ic.Wand size={18} className="text-ink-500" />
      </div>
      <div className="mt-4 text-[13px] text-ink-500">
        Chargement de la fiche…
      </div>
    </div>
  </div>
);

// ─── PDF generation overlay ─────────────────────────────────────────────
// Full-screen backdrop + card centré. z-50 pour passer au-dessus du
// TopHeader sticky. backdrop-blur adouci pour ne pas masquer la fiche
// (l'agent garde un repère visuel du bien pendant que ça génère).
// Spinner en CSS pur (border animate-spin) — pas de lib extra.

const PdfGeneratingOverlay = () => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-ink-900/20 backdrop-blur-sm">
    <div className="bg-paper rounded-2xl shadow-pop px-8 py-7 max-w-[360px] text-center">
      <div className="w-12 h-12 mx-auto rounded-full border-2 border-ink-200 border-t-ink-900 animate-spin" />
      <div className="mt-4 text-[15px] font-semibold text-ink-900">
        Génération du PDF…
      </div>
      <div className="mt-2 text-[12.5px] text-ink-500 leading-relaxed">
        On rassemble les données en direct : cadastre, règlement PLU,
        Géorisques, DVF, environnement. Comptez 10 à 90 secondes selon
        la disponibilité des sources publiques.
      </div>
    </div>
  </div>
);

const MissingState = ({
  onNew,
  onHome,
  authMissing,
}: {
  onNew: () => void;
  onHome: () => void;
  authMissing: boolean;
}) => (
  <div className="bg-paper rounded-[20px] shadow-pop p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-ink-50 hairline grid place-items-center mx-auto">
      <Ic.Cross size={16} className="text-ink-500" />
    </div>
    <h2 className="mt-4 text-[22px] font-semibold tracking-tight">
      {authMissing ? "Connexion requise" : "Fiche introuvable"}
    </h2>
    <p className="mt-2 text-[13px] text-ink-600 max-w-[420px] mx-auto">
      {authMissing
        ? "Connectez-vous pour consulter cette fiche."
        : "Cette fiche n’existe pas, ou vous n’y avez pas accès."}
    </p>
    <div className="mt-5 flex items-center justify-center gap-2">
      <Button variant="soft" onClick={onHome}>
        Retour au dashboard
      </Button>
      <Button variant="dark" onClick={onNew} iconRight={<Ic.Arrow size={13} />}>
        Nouvelle analyse
      </Button>
    </div>
  </div>
);

/* ---------- Real fiche sheet ---------- */

function computePotentialM2(f: FicheDoc): number | null {
  const empriseMaxPct = f.reglement?.emprise_au_sol_max_pct;
  const surface = f.parcel?.surface_m2;
  const existingEmprise = f.existing?.emprise_m2 ?? null;
  if (empriseMaxPct == null || !surface) return null;
  const maxEmprise = (empriseMaxPct / 100) * surface;
  if (existingEmprise == null) return Math.round(maxEmprise);
  const residual = maxEmprise - existingEmprise;
  return residual > 0 ? Math.round(residual) : 0;
}

const FicheSheet = ({
  fiche,
  onDownloadPdf,
  pdfBusy,
}: {
  fiche: FicheDoc;
  onDownloadPdf: () => void;
  pdfBusy: boolean;
}) => {
  const potential = computePotentialM2(fiche);
  const createdAt = fiche.createdAt?.toDate() ?? null;
  const title = ficheHeadline(fiche);
  const subtitle = fiche.geocode.city
    ? `${fiche.geocode.postcode} ${fiche.geocode.city}`
    : fiche.geocode.label;

  // Tab state lives under `?view=` so the URL is shareable/refreshable.
  // Init once from the current URL; subsequent tab clicks push via
  // history.replaceState so we don't pollute the back-stack.
  const [view, setView] = useState<FicheView>(() =>
    typeof window !== "undefined"
      ? parseFicheView(window.location.search)
      : "fiche",
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const qs = viewToQueryString(window.location.search, view);
    const next = window.location.pathname + qs + window.location.hash;
    if (next !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(null, "", next);
    }
  }, [view]);

  // Sticky tab bar — detect when the tabs wrapper has "stuck" to the top of
  // the viewport so we can fade in a subtle border + shadow. A 0-height
  // sentinel placed right above the sticky wrapper is watched via
  // IntersectionObserver with a rootMargin matching the TopHeader height
  // (sm:h-16 = 64 px — we use 64 for all breakpoints, the 8 px early-trigger
  // on mobile is imperceptible). When the sentinel scrolls out of the
  // "viewport minus header" zone, the tabs are effectively stuck.
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = stickySentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-64px 0px 0px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    // `overflow-clip` au lieu de `overflow-hidden` : même effet visuel
    // (clip les enfants aux coins arrondis) mais **sans créer de
    // containing block** qui casserait `position: sticky` pour les
    // tabs descendantes. overflow-hidden crée un scroll container →
    // sticky ne voit plus le scroll du viewport. overflow-clip clip
    // sans introduire de context scroll. Support : tous navigateurs
    // modernes (Chrome 90+, Firefox 81+, Safari 15.4+).
    <div className="bg-paper rounded-[20px] shadow-pop overflow-clip">
      {/* Sheet header */}
      <div className="px-4 sm:px-6 md:px-10 pt-6 sm:pt-10 pb-4 sm:pb-6 flex items-start justify-between gap-4 sm:gap-8 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Pill tone="lime" icon={<Ic.Dot size={8} />}>
              Fiche Potentiel
            </Pill>
            <span className="text-[11.5px] font-mono text-ink-500">
              {fiche.id} · {formatDateLong(createdAt)}
            </span>
          </div>
          <h1 className="mt-3 text-[22px] sm:text-[28px] md:text-[36px] font-semibold tracking-tight leading-tight max-w-[720px]">
            {title}
            <br />
            <span className="text-ink-600 font-normal font-serif italic">
              {subtitle}
            </span>
          </h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Pill tone="soft">Parcelle {parcelLabel(fiche.parcel)}</Pill>
            <Pill tone="soft">Zone {fiche.zone.libelle}</Pill>
            <Pill tone="soft">Surface {fiche.parcel.surface_m2} m²</Pill>
            {fiche.existing?.niveaux && (
              <Pill tone="soft">{fiche.existing.niveaux}</Pill>
            )}
          </div>
        </div>

        <AgentCard initials={fiche.agent} />
      </div>

      {/* Hero KPI banner */}
      <div className="mx-4 sm:mx-6 md:mx-10 rounded-[12px] sm:rounded-[16px] bg-ink-900 text-white relative overflow-hidden p-6 sm:p-8 md:p-10">
        <div className="absolute -right-10 -top-10 w-[280px] h-[280px] rounded-full bg-lime-400 opacity-[0.18] blur-2xl" />
        <div className="absolute right-10 top-10 text-[10px] font-mono tracking-widest uppercase text-lime-300/80">
          Potentiel principal
        </div>
        <div className="relative grid md:grid-cols-3 gap-8 items-end">
          <div className="md:col-span-2">
            <div className="text-[13px] text-ink-300">
              {potential != null
                ? "Emprise au sol constructible résiduelle"
                : "Règlement applicable"}
            </div>
            <div className="mt-1 flex items-baseline gap-4">
              <span className="text-[52px] sm:text-[80px] md:text-[128px] leading-[0.9] font-semibold tracking-[-0.04em] tabular">
                {potential != null ? `+${potential}` : fiche.zone.libelle}
              </span>
              <span className="text-[20px] text-ink-300 pb-4">
                {potential != null ? "m² au sol" : fiche.zone.typezone}
              </span>
            </div>
            <div className="mt-4 text-[15px] text-ink-200 max-w-[540px] leading-relaxed">
              {potential != null && potential > 0 ? (
                <>
                  Calcul indicatif basé sur{" "}
                  <span className="text-lime-300 font-semibold">
                    emprise max {fiche.reglement.emprise_au_sol_max_pct} %
                  </span>{" "}
                  de la parcelle ({fiche.parcel.surface_m2} m²)
                  {fiche.existing?.emprise_m2
                    ? ` − emprise actuelle ${fiche.existing.emprise_m2} m²`
                    : ""}
                  .
                </>
              ) : potential === 0 ? (
                <>Emprise déjà saturée selon le règlement PLU.</>
              ) : (
                <>
                  Règlement extrait pour la zone {fiche.zone.libelle} —
                  consultez les règles ci-dessous.
                </>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <HeroMini
              k="Zone PLU"
              v={fiche.zone.libelle}
              tone="lime"
            />
            <HeroMini
              k="Type de zone"
              v={fiche.zone.typezone || "—"}
            />
            <HeroMini
              k="Emprise max"
              v={
                fiche.reglement.emprise_au_sol_max_pct != null
                  ? `${fiche.reglement.emprise_au_sol_max_pct} %`
                  : "—"
              }
              source={fiche.reglement.sources?.emprise_au_sol_max_pct ?? null}
            />
          </div>
        </div>
      </div>

      {/* View switcher — sticky below TopHeader. The sentinel sits at the
          natural top of the sticky wrapper ; when it scrolls out of the
          "viewport minus 64 px" zone, we know the tabs have locked and we
          fade in a subtle border + soft drop-shadow to separate the bar
          from the scrolling content beneath (pattern Notion/Linear).
          `overflow-x-auto` is moved to the INNER wrapper so it doesn't
          interfere with sticky — an `overflow-x` ancestor would break
          sticky positioning. */}
      <div ref={stickySentinelRef} aria-hidden className="h-0" />
      <div
        className={`sticky top-14 sm:top-16 z-10 px-4 sm:px-8 md:px-10 pt-6 sm:pt-8 pb-3 bg-paper/90 backdrop-blur-md transition-[box-shadow,border-color] duration-200 border-b ${
          stuck
            ? "border-ink-200/70 shadow-[0_4px_12px_-8px_rgba(0,0,0,0.12)]"
            : "border-transparent"
        }`}
      >
        <div className="flex justify-center overflow-x-auto">
          <FicheTabs active={view} onChange={setView} />
        </div>
      </div>

      {/* Body — content switches per tab. The sheet header + hero KPI above
          stay identical so the user keeps visual anchor on the bien. */}
      {view === "env" && (
        <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-8">
          <EnvironnementTab fiche={fiche} />
        </div>
      )}
      {view === "ai" && (
        <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-8">
          <AnalyseIATab fiche={fiche} />
        </div>
      )}
      {view === "fiche" && (
      <div className="px-4 sm:px-6 md:px-10 py-6 md:py-10 space-y-10 sm:space-y-12">
        {/* 01 · Le bien actuel */}
        <Section
          label="01 · Le bien actuel"
          title="Adresse, parcelle, caractéristiques."
        >
          <div className="space-y-4">
            {/* Row 1: parcel map + address/parcel details (50/50 on lg). */}
            <div className="grid lg:grid-cols-2 gap-4">
              <LocationViews
                parcel={fiche.parcel}
                lat={fiche.geocode.lat}
                lng={fiche.geocode.lng}
                label={fiche.geocode.label}
                className="h-[320px] lg:h-auto lg:min-h-[320px]"
              />
              <Card className="p-5">
                <div className="text-[12px] font-semibold uppercase tracking-widest text-ink-500">
                  Adresse & parcelle
                </div>
                <ul className="mt-3 space-y-3 text-[13px]">
                  <KVLine k="Adresse" v={fiche.geocode.label} />
                  <KVLine
                    k="Commune"
                    v={`${fiche.geocode.city} (${fiche.geocode.insee})`}
                  />
                  <KVLine
                    k="Parcelle"
                    v={parcelLabel(fiche.parcel)}
                  />
                  <KVLine
                    k="Surface parcelle"
                    v={`${fiche.parcel.surface_m2.toLocaleString("fr-FR")} m²`}
                  />
                  <li className="flex flex-col gap-1">
                    <span className="text-ink-500">Zone PLU</span>
                    <span className="font-medium">
                      <span className="tabular">{fiche.zone.libelle}</span>
                      {fiche.zone.libelong ? (
                        <span className="text-ink-700"> — {fiche.zone.libelong}</span>
                      ) : null}
                    </span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Row 2: Existant laid out horizontally — 6 short fields each get
                their own column on desktop, 2 per row on mobile. */}
            <Card className="p-5">
              <div className="text-[12px] font-semibold uppercase tracking-widest text-ink-500">
                Existant
              </div>
              <dl className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-3 text-[13px]">
                <KVStack k="Type" v={fiche.existing?.type ?? "—"} />
                <KVStack
                  k="Surface habitable"
                  v={mv(fiche.existing?.surface_habitable_m2, "m²")}
                />
                <KVStack
                  k="Emprise"
                  v={mv(fiche.existing?.emprise_m2, "m²")}
                  source={fiche.existing?.sources?.emprise_m2}
                />
                <KVStack
                  k="Hauteur"
                  v={mv(fiche.existing?.hauteur_m, "m")}
                  source={fiche.existing?.sources?.hauteur_m}
                />
                <KVStack
                  k="Niveaux"
                  v={fiche.existing?.niveaux ?? "—"}
                />
                <KVStack
                  k="Année"
                  v={fiche.existing?.year_built?.toString() ?? "—"}
                />
              </dl>
            </Card>
          </div>
        </Section>

        {/* 02 · Ce que permet le PLU */}
        <Section
          label="02 · Ce que permet le PLU"
          title="Lecture du règlement pour ce bien."
        >
          <Scenarios fiche={fiche} />
        </Section>

        {/* 03 · Règles */}
        <Section
          label="03 · Règles d’urbanisme applicables"
          title={`Zone ${fiche.zone.libelle} — extrait structuré du règlement.`}
        >
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-[13px] table-fixed min-w-[480px]">
              <colgroup>
                <col className="w-[26%]" />
                <col className="w-[46%]" />
                <col className="w-[28%]" />
              </colgroup>
              <thead>
                <tr className="bg-ink-50 text-ink-600 text-left">
                  <th className="px-5 py-3 font-medium">Règle</th>
                  <th className="px-5 py-3 font-medium">Valeur extraite</th>
                  <th className="px-5 py-3 font-medium">Bien / parcelle</th>
                </tr>
              </thead>
              <tbody>
                <RuleRow
                  rule="Emprise au sol max"
                  val={pctOrNull(fiche.reglement.emprise_au_sol_max_pct)}
                  now={formatEmpriseCompliance(fiche)}
                  compliance={empriseCompliance(fiche)}
                  source={fiche.reglement.sources?.emprise_au_sol_max_pct ?? null}
                />
                <RuleRow
                  rule="Coefficient CES max"
                  val={
                    fiche.reglement.ces_max != null
                      ? fiche.reglement.ces_max.toFixed(2)
                      : null
                  }
                  source={fiche.reglement.sources?.ces_max ?? null}
                />
                <RuleRow
                  rule="Hauteur maximale"
                  val={
                    fiche.reglement.hauteur_max_m != null
                      ? `${fiche.reglement.hauteur_max_m} m`
                      : fiche.reglement.hauteur_max_etages
                  }
                  now={formatHauteurCompliance(fiche)}
                  compliance={hauteurCompliance(fiche)}
                  source={
                    fiche.reglement.sources?.hauteur_max_m ??
                    fiche.reglement.sources?.hauteur_max_etages ??
                    null
                  }
                />
                <RuleRow
                  rule="Retrait voirie"
                  val={mv(fiche.reglement.recul_voie_m, "m")}
                  source={fiche.reglement.sources?.recul_voie_m ?? null}
                />
                <RuleRow
                  rule="Retrait limites séparatives"
                  val={mv(fiche.reglement.recul_limites_m, "m")}
                  source={fiche.reglement.sources?.recul_limites_m ?? null}
                />
                <RuleRow
                  rule="Division parcellaire min"
                  val={mv(
                    fiche.reglement.division_parcellaire_min_m2,
                    "m²",
                  )}
                  now={`${fiche.parcel.surface_m2.toLocaleString("fr-FR")} m²`}
                  source={
                    fiche.reglement.sources?.division_parcellaire_min_m2 ?? null
                  }
                />
                <RuleRow
                  rule="Espaces verts min"
                  val={pctOrNull(fiche.reglement.espaces_verts_min_pct)}
                  source={fiche.reglement.sources?.espaces_verts_min_pct ?? null}
                />
                <RuleRow
                  rule="Stationnement"
                  val={fiche.reglement.stationnement}
                  source={fiche.reglement.sources?.stationnement ?? null}
                />
                <RuleRow
                  rule="Annexes"
                  val={fiche.reglement.annexes}
                  source={fiche.reglement.sources?.annexes ?? null}
                />
              </tbody>
            </table>
            </div>
          </Card>

          {fiche.reglement.destinations_autorisees?.length > 0 && (
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <Card className="p-5">
                <div className="text-[11.5px] uppercase tracking-widest text-ink-500 font-semibold">
                  Destinations autorisées
                </div>
                <ul className="mt-3 space-y-1.5 text-[13px]">
                  {fiche.reglement.destinations_autorisees.map((d) => (
                    <li key={d} className="flex items-start gap-2 leading-relaxed">
                      <span className="shrink-0 w-4 h-[18px] inline-flex items-center justify-center text-forest">
                        <Ic.Check size={12} />
                      </span>
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </Card>
              {fiche.reglement.destinations_interdites?.length > 0 && (
                <Card className="p-5">
                  <div className="text-[11.5px] uppercase tracking-widest text-ink-500 font-semibold">
                    Destinations interdites
                  </div>
                  <ul className="mt-3 space-y-1.5 text-[13px]">
                    {fiche.reglement.destinations_interdites.map((d) => (
                      <li key={d} className="flex items-start gap-2 leading-relaxed">
                        <span className="shrink-0 w-4 h-[18px] inline-flex items-center justify-center text-brick">
                          <Ic.Cross size={12} />
                        </span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          {fiche.reglement.raw_sections?.length > 0 && (
            // Collapsed by default — the structured table above + per-row
            // source badges already cover the citations agents need most
            // of the time. The full raw text stays one click away for the
            // 5 % of cases where the agent wants to review every article.
            <details className="mt-4 bg-ink-50 rounded-xl2 p-4">
              <summary className="cursor-pointer text-[13px] font-semibold flex items-center gap-2">
                <Ic.File size={13} /> Extraits du règlement
                <span className="text-ink-500 font-normal">
                  ({fiche.reglement.raw_sections.length} sections)
                </span>
              </summary>
              <div className="mt-4 space-y-4">
                {fiche.reglement.raw_sections.map((s, i) => (
                  <div key={i} className="border-l-2 border-ink-300 pl-4">
                    <div className="text-[12.5px] font-semibold">{s.title}</div>
                    <div className="mt-1 text-[12.5px] text-ink-700 whitespace-pre-wrap leading-relaxed">
                      {s.content}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Q&A panel — the agent can free-form-ask anything the table
              above doesn't cover. Backed by the same `reglement` payload
              we already loaded, so each question is a cheap (~0,02 €)
              Claude call, capped at 20/fiche server-side. */}
          <PluQuestionPanel ficheId={fiche.id} />
        </Section>

        {/* 04 · Servitudes & risques — Géorisques */}
        <Section
          label="04 · Servitudes & risques"
          title="Risques naturels et technologiques à l'adresse."
        >
          <RisquesSection
            ficheId={fiche.id}
            lat={fiche.geocode.lat}
            lng={fiche.geocode.lng}
          />
        </Section>

        {/* 05 · Comparables du marché (DVF) — CEREMA DVF+ */}
        <Section
          label="05 · Comparables du marché (DVF)"
          title="Transactions récentes à proximité (base DGFiP)."
        >
          <DvfSection
            ficheId={fiche.id}
            subject={fiche.parcel}
            lat={fiche.geocode.lat}
            lng={fiche.geocode.lng}
            propertyType={fiche.existing?.type ?? null}
            surfaceBatiM2={fiche.existing?.surface_habitable_m2 ?? null}
          />
        </Section>

        {/* 06 · Démarches */}
        <Section
          label="06 · Démarches à engager"
          title="Selon le scénario retenu."
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-5">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-ink-900 text-lime-300 grid place-items-center">
                  <Ic.File size={15} />
                </div>
                <div>
                  <div className="text-[13px] text-ink-500">
                    Extension / surélévation
                  </div>
                  <div className="text-[16px] font-semibold">
                    Permis de construire
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-[13px] text-ink-700">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-forest" /> Seuil
                  : extension &gt; 20 m² en zone U
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-forest" />{" "}
                  Architecte obligatoire si SDP finale &gt; 150 m²
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-forest" /> Délai
                  d’instruction : ≈ 3 mois
                </li>
              </ul>
            </Card>
            <Card className="p-5 bg-lime-100 !shadow-none">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-ink-900 text-lime-300 grid place-items-center">
                  <Ic.Droplet size={15} />
                </div>
                <div>
                  <div className="text-[13px] text-ink-700">
                    Piscine / extension mineure
                  </div>
                  <div className="text-[16px] font-semibold">
                    Déclaration préalable
                  </div>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-[13px] text-ink-800">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink-900" />{" "}
                  Piscine 10–100 m² non couverte
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink-900" />{" "}
                  Extension ≤ 20 m² (ou 40 m² si PLU le permet)
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink-900" /> Délai
                  d’instruction : ≈ 1 mois
                </li>
              </ul>
            </Card>
          </div>
        </Section>

        {/* Disclaimer */}
        <div className="border-t border-ink-200 pt-6 text-[11px] text-ink-500 leading-relaxed">
          <strong className="text-ink-700">Avertissement —</strong> Cette fiche
          est générée à partir de données publiques (BAN, cadastre IGN,
          Géoportail de l’urbanisme, règlement PLU de {fiche.geocode.city ||
            "la commune"}) croisées automatiquement par Arpentia. Elle constitue
          une indication de potentiel et ne se substitue pas à une étude de
          faisabilité menée par un architecte ou un bureau d’études. La
          délivrance effective d’une autorisation d’urbanisme reste soumise à
          l’instruction de la commune.{" "}
          {fiche.reglement.source?.extractedAt && (
            <>
              Règlement extrait le{" "}
              {formatDateShort(new Date(fiche.reglement.source.extractedAt))}
              {fiche.reglement.source.model
                ? ` via ${fiche.reglement.source.model}`
                : ""}
              .
            </>
          )}
        </div>
      </div>
      )}

      {/* Sticky action footer */}
      <div className="bg-ink-50/80 backdrop-blur border-t border-ink-200 px-10 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[12px] text-ink-500">
          Fiche générée par{" "}
          <strong className="text-ink-800">Arpentia</strong>
          {createdAt && (
            <>
              {" "}
              · {formatWhen(createdAt)}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="soft" size="sm" icon={<Ic.Mail size={13} />}>
            Envoyer au client
          </Button>
          <Button variant="soft" size="sm" icon={<Ic.Share size={13} />}>
            Lien de partage
          </Button>
          <Button
            variant="dark"
            size="sm"
            icon={<Ic.Download size={13} />}
            onClick={onDownloadPdf}
            disabled={pdfBusy}
          >
            {pdfBusy ? "Génération…" : "Télécharger le PDF"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Scenarios computed from reglement + existing ---------- */

const Scenarios = ({ fiche }: { fiche: FicheDoc }) => {
  const potential = computePotentialM2(fiche);
  const { reglement, existing, parcel } = fiche;

  const heightResidual =
    reglement.hauteur_max_m != null && existing?.hauteur_m != null
      ? Math.max(0, reglement.hauteur_max_m - existing.hauteur_m)
      : null;

  const divisionMin = reglement.division_parcellaire_min_m2;
  const canDivide =
    divisionMin != null ? parcel.surface_m2 >= 2 * divisionMin : null;

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <ScenarioCard
        kind="Extension horizontale"
        status={potential == null ? "na" : potential > 0 ? "ok" : "ko"}
        headline={
          potential == null
            ? "Donnée manquante"
            : potential > 0
              ? `+${potential} m² au sol`
              : "Emprise saturée"
        }
        desc={
          potential == null
            ? "L’emprise maximale n’a pas pu être extraite, ou l’emprise actuelle n’est pas renseignée."
            : potential > 0
              ? `Selon l’emprise max de ${reglement.emprise_au_sol_max_pct} % sur ${parcel.surface_m2} m², il reste ~${potential} m² constructibles au sol.`
              : "L’emprise actuelle atteint déjà le plafond PLU. Une extension au sol n’est pas envisageable à règlement constant."
        }
        bullets={[
          reglement.emprise_au_sol_max_pct != null
            ? `Emprise max : ${reglement.emprise_au_sol_max_pct} %`
            : "Emprise max : non extraite",
          reglement.recul_limites_m != null
            ? `Retrait limites : ${reglement.recul_limites_m} m`
            : "",
        ].filter(Boolean)}
        icon={<Ic.Ruler size={16} />}
        tone={potential != null && potential > 0 ? "lime" : undefined}
      />

      <ScenarioCard
        kind="Surélévation"
        status={
          heightResidual == null
            ? "na"
            : heightResidual > 0
              ? "ok"
              : "ko"
        }
        headline={
          heightResidual == null
            ? "Donnée manquante"
            : heightResidual > 0
              ? `+${heightResidual.toFixed(1)} m de hauteur`
              : "Plafond atteint"
        }
        desc={
          heightResidual == null
            ? "Hauteur max PLU et hauteur actuelle nécessaires pour estimer la surélévation."
            : heightResidual > 0
              ? `Hauteur résiduelle de ${heightResidual.toFixed(1)} m avant le plafond PLU (${reglement.hauteur_max_m} m).`
              : "La hauteur actuelle atteint ou dépasse le plafond PLU."
        }
        bullets={[
          reglement.hauteur_max_m != null
            ? `H max PLU : ${reglement.hauteur_max_m} m`
            : reglement.hauteur_max_etages
              ? `H max PLU : ${reglement.hauteur_max_etages}`
              : "H max : non extraite",
          existing?.hauteur_m != null
            ? `H actuelle : ${existing.hauteur_m} m`
            : "H actuelle : non renseignée",
          "Étude structure recommandée",
        ]}
        icon={<Ic.Building size={16} />}
      />

      <ScenarioCard
        kind="Division parcellaire"
        status={canDivide == null ? "na" : canDivide ? "ok" : "ko"}
        headline={
          canDivide == null
            ? "Pas de seuil PLU"
            : canDivide
              ? "Division envisageable"
              : "Non recommandée"
        }
        desc={
          canDivide == null
            ? "Aucune surface minimale de terrain n’a été identifiée au règlement. Depuis la loi ALUR (2014), les PLU ne peuvent plus fixer ce type de seuil — la division reste donc possible sous réserve des autres règles (retraits, accès, réseaux)."
            : canDivide
              ? `Deux lots de ${Math.floor(parcel.surface_m2 / 2)} m² sont possibles au regard du seuil PLU de ${divisionMin} m².`
              : `La parcelle de ${parcel.surface_m2} m² est trop petite : la division entraînerait des lots sous les ${divisionMin} m² exigés.`
        }
        bullets={[
          divisionMin != null
            ? `Seuil minimum : ${divisionMin} m² / lot`
            : "Seuil : non fixé au règlement",
          `Surface parcelle : ${parcel.surface_m2} m²`,
        ]}
        icon={<Ic.Grid size={16} />}
      />

      <ScenarioCard
        kind="Annexes / piscine"
        status={reglement.annexes ? "ok" : "na"}
        headline={
          reglement.annexes
            ? "Règles applicables"
            : "Non détaillé au règlement"
        }
        desc={
          reglement.annexes
            ? reglement.annexes
            : "Les règles relatives aux annexes et piscines n’ont pas été identifiées dans le règlement pour cette zone."
        }
        bullets={[
          reglement.recul_limites_m != null
            ? `Retrait limites : ${reglement.recul_limites_m} m`
            : "",
          "Piscine > 10 m² : déclaration préalable",
          "Piscine > 100 m² : permis de construire",
        ].filter(Boolean)}
        icon={<Ic.Droplet size={16} />}
        tone={reglement.annexes ? "lime" : undefined}
      />
    </div>
  );
};

/* ---------- Demo sheet (when no ficheId) ---------- */

const DemoSheet = ({ onNew }: { onNew: () => void }) => (
  <div className="bg-paper rounded-[20px] shadow-pop p-14 text-center">
    <div className="w-14 h-14 rounded-full bg-lime-200 grid place-items-center mx-auto">
      <Ic.Wand size={18} />
    </div>
    <h2 className="mt-4 text-[24px] font-semibold tracking-tight">
      Aucune fiche ouverte
    </h2>
    <p className="mt-2 text-[13px] text-ink-600 max-w-[460px] mx-auto leading-relaxed">
      Lancez une nouvelle analyse pour obtenir une fiche Potentiel complète :
      zone PLU, règlement, scénarios, et recommandations.
    </p>
    <div className="mt-6 flex items-center justify-center gap-2">
      <Button
        variant="dark"
        onClick={onNew}
        iconRight={<Ic.Arrow size={13} />}
      >
        Nouvelle analyse
      </Button>
    </div>
  </div>
);

/* ---------- bits / helpers ---------- */

const Section = ({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: ReactNode;
}) => (
  <section>
    <div className="mb-5 flex items-end justify-between flex-wrap gap-3">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-500">
          {label}
        </div>
        <h2 className="mt-1.5 text-[22px] font-semibold tracking-tight">
          {title}
        </h2>
      </div>
    </div>
    {children}
  </section>
);

const HeroMini = ({
  k,
  v,
  tone,
  source,
}: {
  k: string;
  v: string;
  tone?: "lime";
  /** Optional citation. When present, an "i" badge is rendered next to the
   *  value ; hover/click reveals the article reference + verbatim excerpt
   *  from the PLU. Builds trust by letting the agent point to the exact
   *  source line when challenged by a client. */
  source?: ReglementSource | null;
}) => (
  <div
    className={`rounded-xl2 p-4 ${
      tone === "lime" ? "bg-lime-400 text-ink-900" : "bg-ink-800 text-white"
    }`}
  >
    <div
      className={`text-[10.5px] uppercase tracking-widest ${
        tone === "lime" ? "text-ink-800" : "text-ink-400"
      }`}
    >
      {k}
    </div>
    <div className="text-[15px] font-semibold mt-1 inline-flex items-center gap-1.5">
      <span>{v}</span>
      {source && <SourceBadge source={source} variant={tone === "lime" ? "dark" : "light"} />}
    </div>
  </div>
);

/**
 * Compact "i" badge that reveals a citation popover on hover/click. Used
 * next to extracted règlement values to surface their provenance — the
 * specific article + verbatim excerpt that justifies the displayed value.
 *
 * Why : agents need to defend the extracted data to clients ("où c'est
 * écrit, ça ?"). Without an audit trail, the trust ceiling is whatever
 * the agent extends to "magic AI numbers" — usually quite low. With
 * one click, they can pull up the actual PLU sentence and stand behind
 * the figure with confidence.
 *
 * Implementation note : the popover is rendered via a portal to
 * `document.body` and positioned with `getBoundingClientRect` —
 * `position: absolute` was being clipped by the table's `overflow-x-auto`
 * wrapper (and similar overflow contexts elsewhere). Portal escapes
 * every parent overflow.
 */
const POPOVER_W = 320;
const POPOVER_GAP = 8;
const POPOVER_VIEWPORT_PAD = 12;

function SourceBadge({
  source,
  variant = "light",
}: {
  source: ReglementSource;
  variant?: "light" | "dark";
}) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number; placement: "below" | "above" } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimeoutRef = useRef<number | null>(null);

  const cancelClose = () => {
    if (closeTimeoutRef.current != null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimeoutRef.current = window.setTimeout(() => setOpen(false), 150);
  };

  const computeAnchor = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return null;
    // Estimate popover height for above/below decision. Most popovers are
    // ~120-180 px ; use 220 as a safe upper bound for the placement check.
    const estHeight = 220;
    const placement: "below" | "above" =
      rect.bottom + estHeight + POPOVER_GAP > window.innerHeight &&
      rect.top - estHeight - POPOVER_GAP > 0
        ? "above"
        : "below";
    const top =
      placement === "above"
        ? rect.top - POPOVER_GAP // popover bottom anchored above button
        : rect.bottom + POPOVER_GAP;
    // Right-align with button by default ; clamp to viewport edges.
    let left = rect.right - POPOVER_W;
    if (left < POPOVER_VIEWPORT_PAD) left = POPOVER_VIEWPORT_PAD;
    if (left + POPOVER_W > window.innerWidth - POPOVER_VIEWPORT_PAD) {
      left = window.innerWidth - POPOVER_W - POPOVER_VIEWPORT_PAD;
    }
    return { top, left, placement };
  };

  const handleOpen = () => {
    cancelClose();
    const a = computeAnchor();
    if (!a) return;
    setAnchor(a);
    setOpen(true);
  };

  // Close on scroll / resize — recomputing the anchor every scroll frame
  // would be possible but adds complexity ; closing is the simpler robust
  // behaviour and matches what most apps do.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  // Cleanup pending timeout on unmount.
  useEffect(() => () => cancelClose(), []);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={handleOpen}
        onMouseLeave={scheduleClose}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else handleOpen();
        }}
        className={`w-[18px] h-[18px] rounded-full inline-flex items-center justify-center text-[10px] font-semibold transition leading-none shrink-0 ${
          variant === "dark"
            ? "bg-ink-900/15 hover:bg-ink-900/30 text-ink-900"
            : "bg-white/15 hover:bg-white/30 text-white"
        }`}
        aria-label="Voir la source dans le règlement"
        title="Voir la source"
      >
        i
      </button>
      {open &&
        anchor &&
        createPortal(
          <div
            role="tooltip"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            style={{
              position: "fixed",
              top: anchor.placement === "above" ? undefined : anchor.top,
              bottom:
                anchor.placement === "above"
                  ? window.innerHeight - anchor.top
                  : undefined,
              left: anchor.left,
              width: POPOVER_W,
            }}
            className="z-[60] bg-ink-900 text-white rounded-lg shadow-pop p-3.5 text-[12px] leading-relaxed normal-case tracking-normal text-left font-normal"
          >
            {source.article && (
              <div className="text-[10.5px] font-mono uppercase tracking-widest text-lime-300 mb-1.5">
                {source.article}
              </div>
            )}
            <div className="italic text-white/95">« {source.excerpt} »</div>
            {source.derived && source.derivationNote && (
              <div className="mt-2 pt-2 border-t border-white/15 text-[11.5px] text-white/75">
                <span className="font-semibold">Valeur déduite : </span>
                {source.derivationNote}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

const KVLine = ({ k, v }: { k: string; v: string }) => (
  <li className="flex items-center justify-between gap-3">
    <span className="text-ink-500 shrink-0">{k}</span>
    <span className="font-medium tabular text-right truncate">{v}</span>
  </li>
);

// Stacked variant — label above value. Used where horizontal space is tight
// (e.g. the 6-column Existant grid) so long values don't get truncated.
// `source` affiche un discret "BD TOPO" sous la valeur pour les champs
// pré-remplis automatiquement — invite à la vérification sans alourdir le
// visuel (pas de pill, juste un label mono-espacé en gris clair).
const KVStack = ({
  k,
  v,
  source,
}: {
  k: string;
  v: string;
  source?: "bdtopo";
}) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <dt className="text-[11px] text-ink-500 uppercase tracking-wider truncate">
      {k}
    </dt>
    <dd className="font-medium tabular text-ink-900 truncate">{v}</dd>
    {source === "bdtopo" && (
      <div
        className="text-[9.5px] font-mono uppercase tracking-widest text-ink-400"
        title="Mesuré automatiquement sur la couche bâtiment IGN BD TOPO — à vérifier sur site."
      >
        BD TOPO
      </div>
    )}
  </div>
);

const ScenarioCard = ({
  kind,
  status,
  headline,
  desc,
  bullets,
  icon,
  tone,
}: {
  kind: string;
  status: "ok" | "ko" | "na";
  headline: string;
  desc: string;
  bullets: string[];
  icon: ReactNode;
  tone?: "lime";
}) => (
  <Card
    className={`p-6 ${tone === "lime" ? "bg-lime-200 !shadow-none border border-[rgba(20,22,26,0.06)]" : ""}`}
  >
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-paper hairline grid place-items-center">
        {icon}
      </div>
      <div className="text-[13px] text-ink-600">{kind}</div>
      <div className="ml-auto">
        {status === "ok" ? (
          <Pill tone="green" icon={<Ic.Check size={11} />}>
            Possible
          </Pill>
        ) : status === "ko" ? (
          <Pill tone="red" icon={<Ic.Cross size={11} />}>
            Non recommandé
          </Pill>
        ) : (
          <Pill tone="soft">Donnée manquante</Pill>
        )}
      </div>
    </div>
    <div className="mt-4 text-[22px] font-semibold tracking-tight">
      {headline}
    </div>
    <p className="mt-2 text-[13px] text-ink-700 leading-relaxed">{desc}</p>
    <ul className="mt-3 space-y-1.5 text-[12.5px]">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-ink-900" />
          {b}
        </li>
      ))}
    </ul>
  </Card>
);

const RuleRow = ({
  rule,
  val,
  now,
  compliance,
  source,
}: {
  rule: string;
  val: string | number | null;
  now?: string;
  compliance?: "ok" | "warn" | "ko" | null;
  /** Citation for this rule. Renders an "i" badge after the value when
   *  present — clicking shows the article + verbatim PLU excerpt. */
  source?: ReglementSource | null;
}) => (
  <tr className="border-t border-ink-200/70 align-top">
    <td className="px-5 py-3">{rule}</td>
    <td className="px-5 py-3 font-medium tabular whitespace-pre-wrap break-words leading-relaxed">
      {val === null || val === "" ? (
        <span className="text-ink-400 italic">non extrait</span>
      ) : (
        <span className="inline-flex items-baseline gap-1.5 flex-wrap">
          <span>{val}</span>
          {source && (
            <span className="self-center">
              <SourceBadge source={source} variant="dark" />
            </span>
          )}
        </span>
      )}
    </td>
    <td className="px-5 py-3 text-ink-700 tabular whitespace-pre-wrap break-words">
      {now ? (
        <span className="inline-flex items-center gap-1.5">
          {compliance && (
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                compliance === "ok"
                  ? "bg-emerald-500"
                  : compliance === "warn"
                    ? "bg-amber-500"
                    : "bg-rose-500"
              }`}
              aria-hidden
            />
          )}
          <span>{now}</span>
        </span>
      ) : (
        <span className="text-ink-400">—</span>
      )}
    </td>
  </tr>
);

// ─── Compliance helpers for the règles table "Bien" column ───────────────
// Where the PLU rule + the existing bien give us enough, we show the bien
// value alongside the ratio vs. the max, plus a colored dot (green <70%,
// amber 70-100%, rose >100%).

function empriseCompliance(f: FicheDoc): "ok" | "warn" | "ko" | null {
  const max = f.reglement.emprise_au_sol_max_pct;
  const emp = f.existing?.emprise_m2;
  const surf = f.parcel?.surface_m2;
  if (max == null || emp == null || !surf) return null;
  const actualPct = (emp / surf) * 100;
  if (actualPct > max) return "ko";
  if (actualPct / max >= 0.7) return "warn";
  return "ok";
}

function formatEmpriseCompliance(f: FicheDoc): string {
  const emp = f.existing?.emprise_m2;
  const surf = f.parcel?.surface_m2;
  if (emp == null) return "—";
  if (!surf) return `${emp} m²`;
  const pct = Math.round((emp / surf) * 100);
  return `${emp} m² · ${pct} %`;
}

function hauteurCompliance(f: FicheDoc): "ok" | "warn" | "ko" | null {
  const max = f.reglement.hauteur_max_m;
  const h = f.existing?.hauteur_m;
  if (max == null || h == null) return null;
  if (h > max) return "ko";
  if (h / max >= 0.85) return "warn";
  return "ok";
}

function formatHauteurCompliance(f: FicheDoc): string {
  const h = f.existing?.hauteur_m;
  if (h == null) return "—";
  return `${h} m`;
}

const PlaceholderBanner = ({
  icon,
  text,
}: {
  icon: ReactNode;
  text: string;
}) => (
  <Card className="p-5 bg-ink-50 !shadow-none">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-paper hairline grid place-items-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[12.5px] font-semibold text-ink-700">
          Bientôt disponible
        </div>
        <div className="mt-1 text-[12.5px] text-ink-600 leading-relaxed">
          {text}
        </div>
      </div>
    </div>
  </Card>
);

const AgentCard = ({ initials }: { initials: string }) => (
  <div className="bg-canvas hairline rounded-xl2 p-4 min-w-[220px]">
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-md bg-ink-900 text-lime-300 grid place-items-center font-bold text-[14px]">
        {initials?.slice(0, 1) || "A"}
      </div>
      <div>
        <div className="text-[12px] font-semibold leading-tight">
          Agent {initials}
        </div>
        <div className="text-[10.5px] text-ink-500">Arpentia</div>
      </div>
    </div>
  </div>
);

/* ---------- formatters ---------- */

function ficheHeadline(f: FicheDoc): string {
  if (f.title) return f.title;
  const type = f.existing?.type ?? "Bien";
  const surf = f.existing?.surface_habitable_m2;
  return surf ? `${type} ${surf} m²` : type;
}

function mv(n: number | null | undefined, unit: string): string {
  if (n == null) return "—";
  return `${n} ${unit}`;
}

function pctOrNull(n: number | null | undefined): string | null {
  if (n == null) return null;
  return `${n} %`;
}

function formatWhen(d: Date | null): string {
  if (!d) return "à l’instant";
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "à l’instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 30) return `il y a ${days} j`;
  return formatDateLong(d);
}

function formatDateLong(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatDateShort(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

// ─── 04 · Risques — Géorisques lazy-loaded card ──────────────────────────
// Fetched on mount (no pipeline snapshot — see src/lib/risques.ts for why).
// Risks are grouped by category, with address-level status taking precedence
// over commune-level for the pill color (a PPRI in force at the address is
// more actionable than a generic "risk exists in commune").
const RisquesSection = ({
  ficheId,
  lat,
  lng,
}: {
  ficheId: string;
  lat: number;
  lng: number;
}) => {
  // `ficheId` active le cache Firestore `/risques/latest` (TTL 30 j) —
  // réutilisé à chaque ouverture, et fallback sur un cache stale si la
  // CF Géorisques tombe en 503 (scénario fréquent côté upstream).
  const state = useRisques(lat, lng, ficheId);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <Card className="p-5 bg-ink-50 !shadow-none">
        <div className="text-[12.5px] text-ink-500 animate-pulse">
          Chargement du rapport Géorisques…
        </div>
      </Card>
    );
  }
  if (state.status === "error") {
    return (
      <Card className="p-5 bg-rose-50 !shadow-none border border-rose-200">
        <div className="text-[12.5px] text-rose-900">
          Impossible de joindre Géorisques. {state.message}
        </div>
      </Card>
    );
  }

  const { report } = state;
  const natureRisks = report.risks.filter((r) => r.category === "naturel");
  const technoRisks = report.risks.filter(
    (r) => r.category === "technologique",
  );
  const presentCount = report.risks.filter((r) => r.present).length;

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <Card className="p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-widest text-ink-500">
            Synthèse
          </div>
          <div className="mt-1 text-[14px] text-ink-900">
            <span className="font-semibold tabular">{presentCount}</span>{" "}
            risque{presentCount > 1 ? "s" : ""} identifié
            {presentCount > 1 ? "s" : ""} sur la commune, sur{" "}
            <span className="tabular">{report.risks.length}</span> catégories
            vérifiées.
          </div>
        </div>
        <a
          href={report.officialReportUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-[12px] font-medium text-ink-700 hover:text-ink-900 underline underline-offset-4"
        >
          Rapport officiel Géorisques
          <Ic.Arrow size={12} />
        </a>
      </Card>

      {/* Natural risks */}
      <RiskGroup title="Risques naturels" risks={natureRisks} />

      {/* Technological risks */}
      <RiskGroup title="Risques technologiques" risks={technoRisks} />
    </div>
  );
};

const RiskGroup = ({
  title,
  risks,
}: {
  title: string;
  risks: RiskEntry[];
}) => {
  // Present risks go first; within each group, sort by address-level severity.
  const sorted = [...risks].sort((a, b) => {
    if (a.present !== b.present) return a.present ? -1 : 1;
    return levelWeight(b.adresseLevel) - levelWeight(a.adresseLevel);
  });
  return (
    <Card className="p-5">
      <div className="text-[12px] font-semibold uppercase tracking-widest text-ink-500">
        {title}
      </div>
      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {sorted.map((r) => (
          <RiskRow key={r.key} risk={r} />
        ))}
      </ul>
    </Card>
  );
};

const RiskRow = ({ risk }: { risk: RiskEntry }) => {
  // Prefer address-level status (more precise). Fall back to commune-level
  // for the label when address-level is null (most risks).
  // When Géorisques returns no libellé but flags the risk as not present, we
  // synthesize an "absent" level so the badge is green rather than grey —
  // matches user expectation that "Absent" means risk confirmed absent.
  const rawLevel = risk.adresseLevel ?? risk.communeLevel;
  const primaryLevel: RiskLevel | null =
    rawLevel ?? (risk.present === false ? "absent" : null);
  const primaryLabel =
    risk.adresseStatus ??
    risk.communeStatus ??
    (risk.present ? "Existe sur la commune" : "Absent");
  return (
    <li className="flex items-center justify-between gap-3 py-2 border-b border-ink-100 last:border-0">
      <span className="text-[13px] text-ink-800 truncate">{risk.libelle}</span>
      <RiskBadge level={primaryLevel} label={primaryLabel} />
    </li>
  );
};

const RiskBadge = ({
  level,
  label,
}: {
  level: RiskLevel | null;
  label: string;
}) => {
  const { bg, fg, dot } = tonesFor(level);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium tabular shrink-0 ${bg} ${fg}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
};

function tonesFor(level: RiskLevel | null): {
  bg: string;
  fg: string;
  dot: string;
} {
  switch (level) {
    case "existant":
      return {
        bg: "bg-rose-100",
        fg: "text-rose-900",
        dot: "bg-rose-600",
      };
    case "existant-faible":
      return {
        bg: "bg-amber-100",
        fg: "text-amber-900",
        dot: "bg-amber-600",
      };
    case "inconnu":
      return {
        bg: "bg-ink-100",
        fg: "text-ink-700",
        dot: "bg-ink-400",
      };
    case "absent":
      return {
        bg: "bg-emerald-100",
        fg: "text-emerald-900",
        dot: "bg-emerald-600",
      };
    default:
      return {
        bg: "bg-ink-100",
        fg: "text-ink-600",
        dot: "bg-ink-300",
      };
  }
}

function levelWeight(level: RiskLevel | null): number {
  switch (level) {
    case "existant":
      return 3;
    case "existant-faible":
      return 2;
    case "inconnu":
      return 1;
    case "absent":
      return 0;
    default:
      return 0;
  }
}

// ─── 05 · Comparables DVF ───────────────────────────────────────────────
// Fetches up to 20 comparables from CEREMA DVF+ filtered by property type
// and surface band, shows a median €/m² headline and a compact table.

const DvfSection = ({
  ficheId,
  subject,
  lat,
  lng,
  propertyType,
  surfaceBatiM2,
}: {
  ficheId: string;
  subject: CadastreParcel;
  lat: number;
  lng: number;
  propertyType: "Maison" | "Immeuble" | "Terrain" | null;
  surfaceBatiM2: number | null;
}) => {
  // The fiche's "existant" type maps 1:1 to our DVF typology except that
  // "Immeuble" is acceptable as-is. We forward the raw type through.
  // `ficheId` active le cache Firestore — réutilisé à chaque ouverture
  // de la fiche pour éviter de rappeler la CF si DVF n'a pas été
  // republié (TTL 90 j).
  const state = useDvfComparables({
    ficheId,
    lat,
    lng,
    propertyType: (propertyType as
      | "Maison"
      | "Appartement"
      | "Immeuble"
      | "Terrain"
      | null) ?? null,
    surfaceBatiM2,
  });

  // Map is opt-in : WMS + Cloud Function call cost a couple hundred ms and
  // most users just need the table. Start collapsed; stay in state so the
  // toggle is remembered while the user stays on the tab.
  const [showMap, setShowMap] = useState(false);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <Card className="p-5 bg-ink-50 !shadow-none">
        <div className="text-[12.5px] text-ink-500 animate-pulse">
          Chargement des comparables DVF…
        </div>
      </Card>
    );
  }
  if (state.status === "error") {
    return (
      <Card className="p-5 bg-rose-50 !shadow-none border border-rose-200">
        <div className="text-[12.5px] text-rose-900">
          Impossible de joindre la base DVF. {state.message}
        </div>
      </Card>
    );
  }

  const c = state.comparables;

  if (c.transactions.length === 0) {
    return (
      <Card className="p-5 bg-ink-50 !shadow-none">
        <div className="text-[12.5px] text-ink-600">
          Aucune transaction comparable trouvée dans un rayon de{" "}
          {c.radius_m} m sur les {new Date().getFullYear() - (c.appliedFilters.sinceYear ?? 0)} dernières années
          {c.appliedFilters.propertyType
            ? ` pour le type « ${c.appliedFilters.propertyType} »`
            : ""}
          .
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Headline: median €/m² */}
      <Card className="p-5 flex flex-wrap items-center justify-between gap-6">
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-widest text-ink-500">
            Médiane €/m² {c.medianPricePerM2Basis === "terrain" ? "(terrain)" : "(bâti)"}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[34px] font-semibold tabular tracking-tight">
              {c.medianPricePerM2
                ? c.medianPricePerM2.toLocaleString("fr-FR")
                : "—"}
            </span>
            <span className="text-[16px] text-ink-500">€/m²</span>
          </div>
          <div className="mt-0.5 text-[11.5px] text-ink-500">
            Sur {c.keptAfterFilter} comparable
            {c.keptAfterFilter > 1 ? "s" : ""} retenu
            {c.keptAfterFilter > 1 ? "s" : ""}
            {c.totalInBbox > c.keptAfterFilter
              ? ` (${c.totalInBbox} transactions totales dans ${c.radius_m} m)`
              : ""}
            .
          </div>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-[11.5px] text-ink-500 max-w-[320px]">
            Filtres appliqués :{" "}
            <span className="text-ink-800 font-medium">
              {c.appliedFilters.propertyType ?? "tous types"}
              {c.appliedFilters.sinceYear
                ? ` · depuis ${c.appliedFilters.sinceYear}`
                : ""}
              {c.appliedFilters.surfaceBatiMinM2 && c.appliedFilters.surfaceBatiMaxM2
                ? ` · ${c.appliedFilters.surfaceBatiMinM2}–${c.appliedFilters.surfaceBatiMaxM2} m²`
                : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setShowMap((v) => !v)}
            aria-pressed={showMap}
            aria-label={
              showMap
                ? "Masquer la carte des comparables"
                : "Afficher les comparables sur une carte"
            }
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-medium transition ${
              showMap
                ? "bg-ink-900 text-lime-300 shadow-pop"
                : "bg-ink-50 text-ink-800 hover:bg-ink-100 hairline"
            }`}
          >
            <Ic.Map size={13} />
            {showMap ? "Masquer la carte" : "Afficher sur carte"}
          </button>
        </div>
      </Card>

      {/* Map: opt-in, lazy-loads parcel geometries through getParcelGeometries */}
      {showMap && (
        <DvfMap
          subject={subject}
          transactions={c.transactions}
          medianPricePerM2={c.medianPricePerM2}
          basis={c.medianPricePerM2Basis}
          className="aspect-[3/2] w-full"
        />
      )}

      {/* Table of comparables */}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-ink-50 text-ink-600 text-left">
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium text-right">Surface</th>
                <th className="px-4 py-2.5 font-medium text-right">Prix</th>
                <th className="px-4 py-2.5 font-medium text-right">€/m²</th>
                <th className="px-4 py-2.5 font-medium text-right">Distance</th>
              </tr>
            </thead>
            <tbody>
              {c.transactions.map((t) => (
                <DvfRow key={t.id} tx={t} basis={c.medianPricePerM2Basis} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const DvfRow = ({
  tx,
  basis,
}: {
  tx: DvfTransaction;
  basis: "bati" | "terrain" | null;
}) => {
  // Prefer whichever basis the headline uses so all rows are comparable.
  const pricePerM2 =
    basis === "terrain" ? tx.price_per_m2_terrain : tx.price_per_m2_bati;
  const surface =
    basis === "terrain" ? tx.surface_terrain_m2 : tx.surface_bati_m2;
  return (
    <tr className="border-t border-ink-100">
      <td className="px-4 py-2.5 tabular text-ink-700">
        {tx.date.slice(0, 10)}
      </td>
      <td className="px-4 py-2.5 text-ink-700 truncate max-w-[200px]">
        {toTitleCase(tx.libtypbien)}
      </td>
      <td className="px-4 py-2.5 tabular text-right">
        {surface ? `${surface.toLocaleString("fr-FR")} m²` : "—"}
      </td>
      <td className="px-4 py-2.5 tabular text-right font-medium">
        {formatEur(tx.valeur_eur)}
      </td>
      <td className="px-4 py-2.5 tabular text-right text-ink-800">
        {pricePerM2 ? `${pricePerM2.toLocaleString("fr-FR")} €` : "—"}
      </td>
      <td className="px-4 py-2.5 tabular text-right text-ink-500">
        {tx.distance_m} m
      </td>
    </tr>
  );
};

function formatEur(n: number): string {
  if (n >= 1_000_000)
    return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2).replace(/\.00$/, "")} M€`;
  if (n >= 1_000) return `${Math.round(n / 1_000).toLocaleString("fr-FR")} k€`;
  return `${n.toLocaleString("fr-FR")} €`;
}

// CEREMA libtypbien comes in ALL CAPS ("UNE MAISON") — tidy to sentence case.
function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/(^|\s|['-])([a-zà-ÿ])/g, (_, sep, ch) => sep + ch.toUpperCase());
}
